import { 
  collection, 
  addDoc, 
  serverTimestamp, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteField
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';

const AUDIT_LOGS_COLLECTION = 'auditLogs';
const SECURITY_EVENTS_COLLECTION = 'securityEvents';

// Security event types
export const SECURITY_EVENT_TYPES = {
  // Authentication events
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',
  LOGOUT: 'logout',
  PASSWORD_RESET: 'password_reset',
  ACCOUNT_LOCKED: 'account_locked',
  
  // Authorization events
  ACCESS_DENIED: 'access_denied',
  PERMISSION_VIOLATION: 'permission_violation',
  ROLE_CHANGE: 'role_change',
  
  // Data access events
  DATA_ACCESS: 'data_access',
  DATA_MODIFICATION: 'data_modification',
  DATA_DELETION: 'data_deletion',
  BULK_OPERATION: 'bulk_operation',
  
  // Security events
  RATE_LIMIT_EXCEEDED: 'rate_limit_exceeded',
  SUSPICIOUS_ACTIVITY: 'suspicious_activity',
  CORS_VIOLATION: 'cors_violation',
  INVALID_INPUT: 'invalid_input',
  XSS_ATTEMPT: 'xss_attempt',
  SQL_INJECTION_ATTEMPT: 'sql_injection_attempt',
  
  // System events
  CONFIGURATION_CHANGE: 'configuration_change',
  BACKUP_CREATED: 'backup_created',
  SYSTEM_MAINTENANCE: 'system_maintenance',
  
  // API events
  API_CALL: 'api_call',
  API_ERROR: 'api_error',
  API_RATE_LIMIT: 'api_rate_limit'
};

// Log a security event
export const logSecurityEvent = async (eventType, details = {}, userId = null) => {
  try {
    const securityEvent = {
      eventType,
      timestamp: serverTimestamp(),
      userId: userId || 'system',
      details: {
        ...details,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        ipAddress: 'client-side', // Will be set by server-side logging
        sessionId: sessionStorage.getItem('sessionId') || 'unknown'
      },
      severity: getEventSeverity(eventType),
      category: getEventCategory(eventType)
    };

    // Firestore rules require auth for writes to securityEvents. Skip Firestore when there is
    // no signed-in user (failed login, password reset before auth, stray initializeSessionTracking,
    // etc.) — avoids permission errors and matches secure defaults.
    const hasAuth = auth?.currentUser != null;
    if (!hasAuth) {
      console.warn(`[SECURITY] ${eventType} (not persisted — no signed-in user):`, securityEvent.details);
      return securityEvent;
    }

    await addDoc(collection(db, SECURITY_EVENTS_COLLECTION), securityEvent);
    
    // Also log to console for immediate visibility
    console.warn(`[SECURITY] ${eventType}:`, securityEvent);
    
    return securityEvent;
  } catch (error) {
    console.error('Error logging security event:', error);
    // Fallback to console logging
    console.error(`[SECURITY ERROR] Failed to log ${eventType}:`, details);
  }
};

// Get event severity level
const getEventSeverity = (eventType) => {
  const highSeverity = [
    SECURITY_EVENT_TYPES.LOGIN_FAILED,
    SECURITY_EVENT_TYPES.ACCESS_DENIED,
    SECURITY_EVENT_TYPES.PERMISSION_VIOLATION,
    SECURITY_EVENT_TYPES.RATE_LIMIT_EXCEEDED,
    SECURITY_EVENT_TYPES.SUSPICIOUS_ACTIVITY,
    SECURITY_EVENT_TYPES.XSS_ATTEMPT,
    SECURITY_EVENT_TYPES.SQL_INJECTION_ATTEMPT
  ];
  
  const mediumSeverity = [
    SECURITY_EVENT_TYPES.DATA_MODIFICATION,
    SECURITY_EVENT_TYPES.DATA_DELETION,
    SECURITY_EVENT_TYPES.ROLE_CHANGE,
    SECURITY_EVENT_TYPES.CORS_VIOLATION,
    SECURITY_EVENT_TYPES.INVALID_INPUT
  ];
  
  if (highSeverity.includes(eventType)) return 'high';
  if (mediumSeverity.includes(eventType)) return 'medium';
  return 'low';
};

// Get event category
const getEventCategory = (eventType) => {
  if (eventType.includes('login') || eventType.includes('logout') || eventType.includes('password')) {
    return 'authentication';
  }
  if (eventType.includes('access') || eventType.includes('permission') || eventType.includes('role')) {
    return 'authorization';
  }
  if (eventType.includes('data') || eventType.includes('modification') || eventType.includes('deletion')) {
    return 'data_access';
  }
  if (eventType.includes('security') || eventType.includes('violation') || eventType.includes('attempt')) {
    return 'security';
  }
  if (eventType.includes('api') || eventType.includes('rate_limit')) {
    return 'api';
  }
  return 'system';
};

// Enhanced audit logging wrapper that preserves backward compatibility
// Accepts either:
// 1) Object form (legacy-friendly): { eventType, description, action, ... }
// 2) Tuple form: (entityType, action, details = {}, userId = null)
// Always writes logs with an `eventType` field so existing UI queries work.
export const logAuditEvent = async (...args) => {
  try {
    // If first arg is an object, delegate directly to legacy logger
    if (args.length === 1 && args[0] && typeof args[0] === 'object' && !Array.isArray(args[0])) {
      return await logAuditEventLegacy(args[0]);
    }

    // Helper: is plain object
    const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
    const [first, second, third, fourth] = args;

    // Case: (eventType, detailsObj)
    if (typeof first === 'string' && isObject(second) && (third === undefined || third === null)) {
      const details = second || {};
      const derivedAction = details.action || inferActionFromEventType(first) || 'update';
      const payload = buildLegacyPayload({
        eventType: first,
        action: derivedAction,
        description: details.description || details.message || prettifyEvent(first),
        details,
        userId: details.userId || null
      });
      return await logAuditEventLegacy(payload);
    }

    // Case: (eventType, descriptionString) with no details
    if (typeof first === 'string' && typeof second === 'string' && (third === undefined || !isObject(third))) {
      const description = second;
      const action = inferActionFromDescription(description) || inferActionFromEventType(first) || 'update';
      const payload = buildLegacyPayload({
        eventType: first,
        action,
        description,
        details: {},
        userId: null
      });
      return await logAuditEventLegacy(payload);
    }

    // Case: (eventType, actionString, detailsObj, userId?)
    if (typeof first === 'string' && typeof second === 'string') {
      const details = isObject(third) ? third : {};
      const userId = isObject(third) ? fourth || null : (third || null);
      const payload = buildLegacyPayload({
        eventType: first,
        action: second,
        description: details.description || details.message || `${prettify(first)} ${prettify(second)}`,
        details,
        userId
      });
      return await logAuditEventLegacy(payload);
    }

    // Fallback: wrap everything we got into details to avoid losing data
    const payload = buildLegacyPayload({
      eventType: typeof first === 'string' ? first : 'system',
      action: typeof second === 'string' ? second : 'update',
      description: 'Logged event',
      details: { raw: args },
      userId: null
    });
    return await logAuditEventLegacy(payload);
  } catch (error) {
    console.error('Error logging audit event:', error);
    return null;
  }
};

// Build a payload for legacy saver with standard metadata
const buildLegacyPayload = ({ eventType, action, description, details, userId }) => {
  return {
    eventType: eventType || 'system',
    action: action || 'update',
    description: description || prettifyEvent(eventType || 'system'),
    details: {
      ...(details || {}),
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      sessionId: sessionStorage.getItem('sessionId') || 'unknown',
      url: window.location.href,
      referrer: document.referrer
    },
    ...(userId ? { userId } : {})
  };
};

// Infer action based on common verbs in a description
const inferActionFromDescription = (text) => {
  if (!text || typeof text !== 'string') return null;
  const lower = text.toLowerCase();
  if (lower.includes('create') || lower.includes('created') || lower.includes('added') || lower.includes('add ')) return 'create';
  if (lower.includes('update') || lower.includes('updated') || lower.includes('edit')) return 'update';
  if (lower.includes('delete') || lower.includes('deleted') || lower.includes('remove') || lower.includes('removed')) return 'delete';
  if (lower.includes('reset')) return 'password_reset';
  if (lower.includes('login')) return 'login';
  if (lower.includes('logout')) return 'logout';
  return null;
};

// Infer action from an eventType string like "payment_created"
const inferActionFromEventType = (eventType) => {
  if (!eventType || typeof eventType !== 'string') return null;
  if (eventType.includes('created') || eventType.startsWith('create_')) return 'create';
  if (eventType.includes('updated') || eventType.startsWith('update_')) return 'update';
  if (eventType.includes('deleted') || eventType.startsWith('delete_')) return 'delete';
  return null;
};

const prettify = (s) => (s || '').toString().replace(/[_-]+/g, ' ').trim();
const prettifyEvent = (s) => {
  const p = prettify(s);
  return p ? p.charAt(0).toUpperCase() + p.slice(1) : 'Event';
};

// Generate unique request ID
const generateRequestId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Get recent security events
export const getRecentSecurityEvents = async (limit = 50) => {
  try {
    const q = query(
      collection(db, SECURITY_EVENTS_COLLECTION),
      orderBy('timestamp', 'desc'),
      limit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching security events:', error);
    return [];
  }
};

// Get security events by type
export const getSecurityEventsByType = async (eventType, limit = 100) => {
  try {
    const q = query(
      collection(db, SECURITY_EVENTS_COLLECTION),
      where('eventType', '==', eventType),
      orderBy('timestamp', 'desc'),
      limit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching security events by type:', error);
    return [];
  }
};

// Get security events by user
export const getSecurityEventsByUser = async (userId, limit = 100) => {
  try {
    const q = query(
      collection(db, SECURITY_EVENTS_COLLECTION),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching security events by user:', error);
    return [];
  }
};

// Get high severity security events
export const getHighSeveritySecurityEvents = async (limit = 50) => {
  try {
    const q = query(
      collection(db, SECURITY_EVENTS_COLLECTION),
      where('severity', '==', 'high'),
      orderBy('timestamp', 'desc'),
      limit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching high severity security events:', error);
    return [];
  }
};

// Create audit log for data access
export const createAuditLog = async (action, message, details = {}) => {
  try {
    const auditLog = {
      action,
      message,
      timestamp: serverTimestamp(),
      details: {
        ...details,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        sessionId: sessionStorage.getItem('sessionId') || 'unknown'
      }
    };

    await addDoc(collection(db, AUDIT_LOGS_COLLECTION), auditLog);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[AUDIT] ${action}: ${message}`, auditLog);
    }
    
    return auditLog;
  } catch (error) {
    console.error('Error creating audit log:', error);
    console.error(`[AUDIT ERROR] Failed to log ${action}: ${message}`, details);
  }
};

// Monitor for suspicious activity
export const monitorSuspiciousActivity = async (userId, action, context = {}) => {
  try {
    // Get recent events for this user
    const recentEvents = await getSecurityEventsByUser(userId, 10);
    
    // Check for patterns that might indicate suspicious activity
    const suspiciousPatterns = [
      // Multiple failed login attempts
      recentEvents.filter(e => e.eventType === SECURITY_EVENT_TYPES.LOGIN_FAILED).length > 3,
      
      // Multiple access denied events
      recentEvents.filter(e => e.eventType === SECURITY_EVENT_TYPES.ACCESS_DENIED).length > 5,
      
      // Rapid succession of actions
      recentEvents.length > 8 && 
      recentEvents[0].timestamp && 
      recentEvents[recentEvents.length - 1].timestamp &&
      (new Date(recentEvents[0].timestamp.toDate()) - new Date(recentEvents[recentEvents.length - 1].timestamp.toDate())) < 60000, // 1 minute
      
      // Unusual data access patterns
      recentEvents.filter(e => e.eventType === SECURITY_EVENT_TYPES.DATA_ACCESS).length > 20
    ];
    
    if (suspiciousPatterns.some(pattern => pattern)) {
      await logSecurityEvent(SECURITY_EVENT_TYPES.SUSPICIOUS_ACTIVITY, {
        userId,
        action,
        context,
        detectedPatterns: suspiciousPatterns,
        recentEventsCount: recentEvents.length
      });
      
      return {
        suspicious: true,
        patterns: suspiciousPatterns,
        recommendation: 'Consider additional authentication or temporary access restrictions'
      };
    }
    
    return { suspicious: false };
  } catch (error) {
    console.error('Error monitoring suspicious activity:', error);
    return { suspicious: false, error: error.message };
  }
};

// Initialize session tracking
export const initializeSessionTracking = () => {
  const sessionId = generateRequestId();
  sessionStorage.setItem('sessionId', sessionId);
  
  // Log session start
  logSecurityEvent(SECURITY_EVENT_TYPES.LOGIN_SUCCESS, {
    sessionId,
    url: window.location.href,
    userAgent: navigator.userAgent
  });
  
  return sessionId;
};

// ============================================================================
// LEGACY FUNCTIONS FOR BACKWARD COMPATIBILITY
// ============================================================================

/**
 * Logs an action to the audit log (legacy function)
 * @param {string|object} eventData - Either the event type string or an object containing eventType, description, and additional data
 * @param {string} [descriptionParam] - Description of the event (only used if eventData is a string)
 * @returns {Promise<void>}
 */
export const logAuditEventLegacy = async (eventData, descriptionParam) => {
  try {
    console.log('logAuditEventLegacy called with:', eventData);
    
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error('No current user found when trying to log audit event');
      return;
    }
    
    let eventType, description, additionalData = {};
    
    // Handle both object parameter and separate parameters
    if (typeof eventData === 'object' && eventData !== null) {
      eventType = eventData.eventType || 'system';
      description = eventData.description;
      // Extract any additional data
      const { eventType: _, description: __, ...rest } = eventData;
      additionalData = rest;
    } else {
      eventType = eventData || 'system';
      description = descriptionParam;
    }
    
    // Ensure we have a valid event type
    if (!eventType || eventType.trim() === '' || eventType === 'Unknown') {
      eventType = 'system';
    }
    
    // Create a meaningful description if none provided
    if (!description || description.trim() === '') {
      const action = eventType === 'login' ? 'logged in' :
                    eventType === 'user' ? 'performed user action' :
                    eventType === 'permission' ? 'changed permissions' :
                    eventType === 'property' ? 'modified property' :
                    eventType === 'payment' ? 'processed payment' :
                    eventType === 'maintenance' ? 'updated maintenance' :
                    eventType === 'tenant' ? 'modified tenant information' :
                    eventType === 'system' ? 'performed system action' :
                    'performed action';
      
      // Add more specific details for tenant actions if available
      if (eventType === 'tenant' && additionalData.tenantName) {
        description = `${action} for tenant: ${additionalData.tenantName}`;
      } else {
        description = `User ${currentUser.email || 'unknown'} ${action} at ${new Date().toLocaleString()}`;
      }
    }
    
    // Get user role if available
    let userRole = 'unknown';
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        userRole = userDoc.data().role || 'unknown';
      }
    } catch (error) {
      console.error('Error getting user role for audit log:', error);
    }
    
    // Create a new audit log entry with sanitized data
    const auditLogRef = doc(collection(db, 'auditLogs'));
    
    // Prepare the audit log data, removing any undefined values
    const auditLogData = {
      timestamp: serverTimestamp(),
      user: `${currentUser.displayName || 'User'} (${currentUser.uid})`,
      userId: currentUser.uid,
      userEmail: currentUser.email || 'unknown@example.com',
      userRole: userRole,
      eventType: eventType,
      description: description,
      ipAddress: window.clientInformation?.userAgentData?.platform || navigator.platform || 'Unknown',
      userAgent: navigator.userAgent
    };
    
    console.log('Prepared audit log data:', auditLogData);
    
    // Add any additional data, filtering out undefined values
    for (const [key, value] of Object.entries(additionalData)) {
      if (value !== undefined) {
        // Special handling for certain fields that should remain as arrays/objects
        if (key === 'changes' && Array.isArray(value)) {
          // Keep changes as an array for better UI display
          auditLogData[key] = value;
        } else if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
          // If the value is an object (but not a Date), stringify it
          auditLogData[key] = JSON.stringify(value);
        } else {
          auditLogData[key] = value;
        }
      }
    }
    
    console.log('Saving audit log to Firestore with data:', auditLogData);
    
    // Save the audit log
    try {
      await setDoc(auditLogRef, auditLogData);
      console.log('Successfully saved audit log with ID:', auditLogRef.id);
      
      // Also track in Google Analytics (non-blocking)
      try {
        const { trackBusinessEvent, trackPropertyEvent, trackPaymentEvent, trackMaintenanceEvent, trackTenantEvent } = await import('../services/analyticsService');
        
        // Map audit event types to analytics tracking
        const action = additionalData.action || eventType;
        
        switch (eventType) {
          case 'property':
            if (additionalData.propertyId) {
              trackPropertyEvent(action, additionalData.propertyId, {
                description,
                ...additionalData
              });
            } else {
              trackBusinessEvent('property_action', { action, description, ...additionalData });
            }
            break;
          case 'payment':
            if (additionalData.paymentId) {
              trackPaymentEvent(action, additionalData.paymentId, additionalData.amount || 0, {
                description,
                ...additionalData
              });
            } else {
              trackBusinessEvent('payment_action', { action, description, ...additionalData });
            }
            break;
          case 'maintenance':
            if (additionalData.maintenanceId) {
              trackMaintenanceEvent(action, additionalData.maintenanceId, {
                description,
                ...additionalData
              });
            } else {
              trackBusinessEvent('maintenance_action', { action, description, ...additionalData });
            }
            break;
          case 'tenant':
            if (additionalData.tenantId) {
              trackTenantEvent(action, additionalData.tenantId, {
                description,
                ...additionalData
              });
            } else {
              trackBusinessEvent('tenant_action', { action, description, ...additionalData });
            }
            break;
          default:
            // Track as general business event
            trackBusinessEvent(eventType, {
              action,
              description,
              ...additionalData
            });
        }
      } catch (analyticsError) {
        // Analytics tracking is non-critical, so we don't fail the audit log
        console.warn('Analytics tracking failed (non-critical):', analyticsError);
      }
      return auditLogRef.id;
    } catch (firestoreError) {
      console.error('Error saving audit log to Firestore:', firestoreError);
      return null;
    }
  } catch (error) {
    console.error('Error logging audit event:', error);
    // Don't throw the error to prevent disrupting the user experience
    return null;
  }
};

/**
 * Creates test audit logs for development purposes
 */
export const createTestAuditLogs = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    
    const eventTypes = ['login', 'user', 'permission', 'property', 'payment', 'maintenance'];
    const descriptions = [
      'User logged in',
      'User profile updated',
      'Permission changed for role',
      'Property added',
      'Payment recorded',
      'Maintenance request updated'
    ];
    
    for (let i = 0; i < eventTypes.length; i++) {
      await logAuditEventLegacy(eventTypes[i], descriptions[i]);
    }
    
    console.log('Test audit logs created successfully');
  } catch (error) {
    console.error('Error creating test audit logs:', error);
  }
};

/**
 * Ensure the auditLogs collection exists by creating test logs if empty
 */
export const ensureAuditLogsCollection = async () => {
  // Check if any audit logs exist already
  const auditLogsRef = collection(db, 'auditLogs');
  const q = query(auditLogsRef, limit(1));
  const snapshot = await getDocs(q);
  
  // If no logs exist, create some diverse test logs (not just in development)
  if (snapshot.empty) {
    console.log('Creating diverse test audit logs...');
    
    // Create a variety of test logs across all event types
    const testLogs = [
      // User Management Events
      {
        eventType: 'user',
        description: 'New user account created: admin@example.com',
        action: 'create_user',
        details: 'User account created with administrator role'
      },
      {
        eventType: 'user',
        description: 'User role changed from Agent to Administrator',
        action: 'role_change',
        details: 'Role elevated for project management'
      },
      
      // Security Events
      {
        eventType: 'security',
        description: 'User login from new device detected',
        action: 'login_new_device',
        details: 'Login attempt from IP: 192.168.1.100'
      },
      {
        eventType: 'security',
        description: 'Password reset requested',
        action: 'password_reset',
        details: 'Password reset token generated and sent'
      },
      
      // Settings Changes
      {
        eventType: 'settings',
        description: 'System branding updated',
        action: 'update_branding',
        details: 'Company logo and theme colors modified'
      },
      {
        eventType: 'settings',
        description: 'Notification preferences updated',
        action: 'update_notifications',
        details: 'Email notifications enabled for maintenance requests'
      },
      
      // Property Management
      {
        eventType: 'property',
        description: 'New property added: 123 Oak Street, London',
        action: 'create_property',
        details: 'Property added with 3 bedrooms, 2 bathrooms',
        propertyAddress: '123 Oak Street, London SW1A 1AA'
      },
      {
        eventType: 'property',
        description: 'Property details updated: 456 Elm Avenue',
        action: 'update_property',
        details: 'Updated rental price from £1200 to £1350'
      },
      
      // Permission Changes
      {
        eventType: 'permission',
        description: 'Role permissions updated for Agent role',
        action: 'update_role_permissions',
        details: 'Granted access to financial reports'
      },
      
      // System Events
      {
        eventType: 'system',
        description: 'Data backup completed successfully',
        action: 'system_backup',
        details: 'Scheduled backup of all property and tenant data'
      },
      
      // Maintenance Events
      {
        eventType: 'maintenance',
        description: 'Maintenance request created: Leaking faucet',
        action: 'create_maintenance',
        details: 'Urgent plumbing repair needed',
        propertyAddress: '789 Pine Street, Birmingham'
      },
      
      // Login Events
      {
        eventType: 'login',
        description: 'Successful user login',
        action: 'login_success',
        details: 'User authenticated successfully'
      },
      
      // Payment Events (keeping some for completeness)
      {
        eventType: 'payment',
        description: 'Rent payment of £1,500.00 for 123 Main Street',
        action: 'rent_payment',
        details: {
          amount: 1500,
          propertyName: '123 Main Street',
          propertyAddress: '123 Main Street, London SW1A 1AA',
          propertyId: 'prop123',
          tenantName: 'John Smith',
          tenantId: 'ten456',
          paymentType: 'rent',
          reference: 'INV-2023-05-001',
          date: new Date(),
          paymentMethod: 'Bank Transfer',
          status: 'completed'
        }
      }
    ];
    
    // Create each test log with a small delay for proper ordering
    for (let i = 0; i < testLogs.length; i++) {
      const log = testLogs[i];
      await logAuditEventLegacy(log);
      console.log(`Created initial audit log ${i + 1}/${testLogs.length}: ${log.eventType} - ${log.description}`);
      
      // Small delay to ensure different timestamps
      if (i < testLogs.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    console.log('Diverse test audit logs created successfully');
  }
};

/**
 * Migration function to copy existing property and tenant audit logs to main audit logs
 */
export const migrateExistingAuditLogs = async () => {
  try {
    console.log('🔄 Starting migration of existing audit logs...');
    
    // First, check if migration has already been run
    const migrationRef = doc(db, 'systemMeta', 'auditLogMigration');
    const migrationDoc = await getDoc(migrationRef);
    
    if (migrationDoc.exists() && migrationDoc.data().completed) {
      console.log('✅ Migration already completed. Skipping...');
      return { success: true, message: 'Migration already completed', migratedCount: 0 };
    }

    let totalMigrated = 0;

    // 1. Migrate Property Audit Logs
    console.log('📄 Migrating property audit logs...');
    const propertyAuditsQuery = query(collection(db, 'propertyAudits'), orderBy('timestamp', 'desc'));
    const propertyAuditsSnapshot = await getDocs(propertyAuditsQuery);
    
    let propertyLogsMigrated = 0;
    for (const docSnap of propertyAuditsSnapshot.docs) {
      const propertyLog = docSnap.data();
      
      // Convert property audit log to main audit log format
      const migratedLog = {
        eventType: 'property',
        description: propertyLog.details || `Property ${propertyLog.action}`,
        action: propertyLog.action,
        details: propertyLog.details,
        
        // Property-specific fields
        propertyId: propertyLog.propertyId,
        
        // User information
        userId: propertyLog.userId,
        userEmail: propertyLog.userEmail,
        userName: propertyLog.userName || propertyLog.userEmail,
        
        // Timestamps
        timestamp: propertyLog.timestamp || serverTimestamp(),
        originalLogId: docSnap.id,
        originalCollection: 'propertyAudits',
        migratedAt: serverTimestamp()
      };

      // Add to main audit logs
      await addDoc(collection(db, 'auditLogs'), migratedLog);
      propertyLogsMigrated++;
    }
    
    console.log(`✅ Migrated ${propertyLogsMigrated} property audit logs`);
    totalMigrated += propertyLogsMigrated;

    // 2. Migrate Tenant Audit Logs
    console.log('👤 Migrating tenant audit logs...');
    const tenantAuditsQuery = query(
      collection(db, 'auditLogs'), 
      where('entityType', '==', 'tenant'),
      orderBy('timestamp', 'desc')
    );
    const tenantAuditsSnapshot = await getDocs(tenantAuditsQuery);
    
    let tenantLogsMigrated = 0;
    for (const docSnap of tenantAuditsSnapshot.docs) {
      const tenantLog = docSnap.data();
      
      // Skip if this is already a migrated log (has migratedAt field)
      if (tenantLog.migratedAt || tenantLog.originalCollection) {
        continue;
      }
      
      // Convert tenant audit log to main audit log format
      const migratedLog = {
        eventType: 'tenant',
        description: tenantLog.details?.action || `Tenant ${tenantLog.action}`,
        action: tenantLog.action,
        details: tenantLog.details,
        
        // Tenant-specific fields
        tenantId: tenantLog.entityId,
        tenantName: tenantLog.tenantName,
        tenantEmail: tenantLog.tenantEmail,
        
        // Property information if available
        ...(tenantLog.propertyId && { propertyId: tenantLog.propertyId }),
        ...(tenantLog.propertyAddress && { propertyAddress: tenantLog.propertyAddress }),
        
        // User information
        userId: tenantLog.userId,
        userEmail: tenantLog.userEmail,
        userName: tenantLog.userName || tenantLog.performedBy || tenantLog.userEmail,
        
        // Timestamps
        timestamp: tenantLog.timestamp || serverTimestamp(),
        originalLogId: docSnap.id,
        originalCollection: 'auditLogs_tenant',
        migratedAt: serverTimestamp()
      };

      // Add to main audit logs (this will create a copy, not move the original)
      await addDoc(collection(db, 'auditLogs'), migratedLog);
      tenantLogsMigrated++;
    }
    
    console.log(`✅ Migrated ${tenantLogsMigrated} tenant audit logs`);
    totalMigrated += tenantLogsMigrated;

    // Mark migration as completed
    await setDoc(migrationRef, {
      completed: true,
      completedAt: serverTimestamp(),
      propertyLogsMigrated: propertyLogsMigrated,
      tenantLogsMigrated: tenantLogsMigrated,
      totalMigrated: totalMigrated
    });

    console.log(`🎉 Migration completed! Total migrated: ${totalMigrated} logs`);
    
    return {
      success: true,
      message: `Successfully migrated ${totalMigrated} audit logs`,
      migratedCount: totalMigrated,
      breakdown: {
        propertyLogs: propertyLogsMigrated,
        tenantLogs: tenantLogsMigrated
      }
    };

  } catch (error) {
    console.error('❌ Error during audit log migration:', error);
    return {
      success: false,
      message: `Migration failed: ${error.message}`,
      error: error
    };
  }
};

// Function to check migration status
export const getMigrationStatus = async () => {
  try {
    const migrationRef = doc(db, 'systemMeta', 'auditLogMigration');
    const migrationDoc = await getDoc(migrationRef);
    
    if (migrationDoc.exists()) {
      return {
        completed: migrationDoc.data().completed || false,
        completedAt: migrationDoc.data().completedAt,
        totalMigrated: migrationDoc.data().totalMigrated || 0,
        breakdown: {
          propertyLogs: migrationDoc.data().propertyLogsMigrated || 0,
          tenantLogs: migrationDoc.data().tenantLogsMigrated || 0
        }
      };
    }
    
    return {
      completed: false,
      totalMigrated: 0,
      breakdown: { propertyLogs: 0, tenantLogs: 0 }
    };
  } catch (error) {
    console.error('Error checking migration status:', error);
    return {
      completed: false,
      error: error.message
    };
  }
};

/**
 * Log a payment activity with detailed information
 * @param {Object} paymentData - Payment information
 * @returns {Promise<Object>} The created audit log document
 */
export const logPaymentActivity = async (paymentData) => {
  try {
    // Ensure we have all required fields
    const paymentDetails = {
      amount: paymentData.amount || 0,
      propertyName: paymentData.propertyName || 'Unknown property',
      propertyAddress: paymentData.propertyAddress,
      propertyId: paymentData.propertyId,
      tenantName: paymentData.tenantName || 'Unknown tenant',
      tenantId: paymentData.tenantId,
      paymentType: paymentData.paymentType || 'rent',
      type: paymentData.type || paymentData.paymentType || 'rent', // For backwards compatibility
      reference: paymentData.reference || null,
      notes: paymentData.notes || null,
      date: paymentData.date || new Date(),
      paymentMethod: paymentData.paymentMethod || 'Unknown method',
      status: paymentData.status || 'completed'
    };

    // Create a user-friendly description based on payment details
    const description = `${paymentDetails.paymentType === 'rent' ? 'Rent payment' : paymentDetails.paymentType.charAt(0).toUpperCase() + paymentDetails.paymentType.slice(1) + ' payment'} of £${parseFloat(paymentDetails.amount).toFixed(2)} for ${paymentDetails.propertyName}`;

    // Also store the raw data directly on the log (for backward compatibility)
    const directProperties = {
      amount: paymentDetails.amount,
      propertyName: paymentDetails.propertyName,
      propertyAddress: paymentDetails.propertyAddress,
      tenantName: paymentDetails.tenantName,
      paymentType: paymentDetails.paymentType
    };

    // Log the payment activity
    return await logAuditEventLegacy({
      eventType: 'payment',
      action: paymentData.action || `${paymentDetails.paymentType}_payment`,
      description,
      details: paymentDetails, // Store structured data in details field
      targetUser: paymentData.tenantEmail, // If available
      success: paymentData.success !== false,
      metadata: paymentData.metadata || {},
      ...directProperties // Also store key properties directly on the log for easy access
    });
  } catch (error) {
    console.error('Error logging payment activity:', error);
    return null;
  }
};

// Export the original function for backward compatibility
export { logAuditEventLegacy as logAuditEventOriginal };

// Log audit event for public portal users (contractors/tenants) without Firebase Auth
// This function directly writes to auditLogs collection without requiring auth.currentUser
export const logPublicPortalAuditEvent = async (eventData) => {
  try {
    const {
      eventType = 'system',
      description = '',
      action = 'update',
      userId = null,
      userEmail = null,
      userName = null,
      userRole = 'contractor',
      details = {},
      propertyId = null,
      entityType = null,
      entityId = null
    } = eventData;

    // Create audit log entry for public portal user
    const auditLogData = {
      timestamp: serverTimestamp(),
      userId: userId || 'public_portal',
      userEmail: userEmail || 'unknown@example.com',
      userName: userName || 'Public Portal User',
      userRole: userRole || 'contractor',
      eventType: eventType,
      action: action,
      description: description || `${eventType} event`,
      details: {
        ...details,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        sessionId: sessionStorage.getItem('sessionId') || 'unknown',
        url: window.location.href,
        referrer: document.referrer,
        portalType: userRole === 'contractor' ? 'contractor_portal' : 'tenant_portal'
      },
      ipAddress: navigator.platform || 'Unknown',
      userAgent: navigator.userAgent,
      ...(propertyId ? { propertyId } : {}),
      ...(entityType ? { entityType } : {}),
      ...(entityId ? { entityId } : {})
    };

    // Remove undefined values
    Object.keys(auditLogData).forEach(key => {
      if (auditLogData[key] === undefined) {
        delete auditLogData[key];
      }
    });

    await addDoc(collection(db, AUDIT_LOGS_COLLECTION), auditLogData);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[AUDIT] Public Portal ${eventType}:`, auditLogData);
    }
    
    return auditLogData;
  } catch (error) {
    console.error('Error logging public portal audit event:', error);
    // Don't throw - allow the app to continue even if audit logging fails
    return null;
  }
}; 